import {useEvent, useRefreshEvent} from 'Event/EventProvider'
import {useOrganization} from 'organization/OrganizationProvider'
import React, {useEffect, useState, useCallback} from 'react'
import Echo from '@ably/laravel-echo'
import {
  socketConnected,
  socketDisconnected,
} from 'organization/Event/EventSocketNotification'
import {useTeamMemberEcho} from 'obvio/auth'

type Channel = ReturnType<Echo['private']>

type EventSocketContextProps = {
  channel: Channel | null
}

const EventSocketContext = React.createContext<
  EventSocketContextProps | undefined
>(undefined)

/**
 * Event names start with a '.' to prevent Laravel's
 * auto name-spacing event.
 *
 * Reference: https://laravel.com/docs/8.x/broadcasting#namespaces
 */
const UPDATED_EVENT = '.event.updated'

/**
 * Subscribes to live updates to the event model. This means
 * multiple people can edit the dashboard at the same
 * time without overwriting each other's changes.
 */
export default function EventSocketProvider(props: {
  children: React.ReactElement
}) {
  const [socketId, setSocketId] = useState<string | null>(null)
  const [channel, setChannel] = useState<Channel | null>(null)

  const {
    event: {slug},
  } = useEvent()
  const echo = useTeamMemberEcho()

  const refreshEvent = useRefreshEvent()

  const {client} = useOrganization()

  const disconnect = useCallback(() => setSocketId(null), [])

  useEffect(() => {
    const channelName = `event.${slug}`
    const privateChannel = echo
      .private(channelName)
      .error((error: {code: number}) => {
        /**
         * Handle authentication failure which would mean we would never receive auth updates
         * Ably error code 40142 = token expired
         * Ably error code 40100 = unauthorized
         */
        if (error.code === 40100 || error.code === 40142) {
          // disconnect()
        }
      })

    const connection = echo.connector.ably.connection

    const onConnected = () => {
      setSocketId(connection.id)
      socketConnected(client, slug, connection.id)
    }

    // If already connected, handle immediately
    if (connection.state === 'connected' && connection.id) {
      onConnected()
    }

    connection.on('connected', onConnected)
    connection.on('disconnected', disconnect)

    setChannel(privateChannel)

    return () => {
      echo.leave(channelName)
      socketDisconnected(client, slug, connection.id)
    }
  }, [client, slug, echo, disconnect])

  useEffect(() => {
    if (!channel || !socketId) {
      return
    }

    channel.listen(
      UPDATED_EVENT,
      (data: {
        updated_at: string // Event updated at timestamp
        id: string // Update identifier for tracking
      }) => {
        refreshEvent({
          updatedAt: data.updated_at,
          id: data.id,
          socketId,
        })
      },
    )

    return () => {
      channel.stopListening(UPDATED_EVENT)
    }
  }, [channel, socketId, refreshEvent])

  return (
    <EventSocketContext.Provider value={{channel}}>
      {props.children}
    </EventSocketContext.Provider>
  )
}

export function useEventSocket() {
  const context = React.useContext(EventSocketContext)
  if (context === undefined) {
    throw new Error('useEventSocket must be used within a EventSocketProvider')
  }

  return context
}
